import React from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Switch,
  Text,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
  Tag,
  keyframes,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { HeaderWithStats } from "services/MetadataServices";
import { AppIcons } from "constants/icons";
import { getRelativeTimeString } from "utils/dateUtils";

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.6); }
  70%  { box-shadow: 0 0 0 5px rgba(72, 187, 120, 0); }
  100% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
`;

export interface JobCardProps {
  job: Header;
  copyingJobId?: string | null;
  isDeleting?: boolean;
  sharedByIds?: string[];
  analystsList?: HeaderWithStats[];
  onJobClick?: (job: Header) => void;
  onDefinitionClick?: (job: Header) => void;
  onPastRunsClick?: (job: Header) => void;
  onToggleActive?: (job: Header, isActive: boolean) => Promise<void>;
  onRunJob?: (job: Header) => void;
  onCopyJob?: (job: Header) => Promise<void>;
  onShareClick?: (job: Header) => void;
  onDeleteClick?: (job: Header) => void;
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  copyingJobId,
  isDeleting,
  sharedByIds = [],
  analystsList = [],
  onJobClick,
  onDefinitionClick,
  onPastRunsClick,
  onToggleActive,
  onRunJob,
  onCopyJob,
  onShareClick,
  onDeleteClick,
}) => {
  const isLive = !!job.has_active_schedule;
  const modifiedDate = job.last_modified_date_ms ? getRelativeTimeString(job.last_modified_date_ms) : null;
  const ownerName = analystsList.find(a => a.id === job.owner_id)?.name;
  const sharedAnalysts = sharedByIds
    .filter(id => id !== job.owner_id)
    .map(id => analystsList.find(a => a.id === id))
    .filter((analyst): analyst is HeaderWithStats => !!analyst);
  const showAnalystTags = !!ownerName || sharedAnalysts.length > 0;
  const hasMenu = !!(onToggleActive || onRunJob || onCopyJob || onShareClick || onDeleteClick);

  const renderAnalystTag = (name: string, key?: string) => (
    <WrapItem key={key}>
      <Tag size="sm" variant="outline" borderColor="gray.400" borderRadius="md" px={2} py={0.5}>
        <HStack spacing={1}>
          <Icon as={AppIcons.analyst} boxSize={3} color="gray.600" />
          <Text fontSize="xs" color="gray.600" fontWeight="500" mb={0} noOfLines={1}>
            {name}
          </Text>
        </HStack>
      </Tag>
    </WrapItem>
  );

  return (
    <Card
      cursor="pointer"
      _hover={{
        shadow: "md",
        borderColor: isLive ? "green.500" : "purple.400",
        bg: "gray.50",
      }}
      transition="all 0.2s"
      border="1px solid"
      borderColor={isLive ? "green.500" : "gray.500"}
      bg="white"
      borderRadius="md"
      onClick={() => onJobClick?.(job)}
    >
      <CardBody py={2.5} px={3}>
        <HStack justify="space-between" align="center" spacing={2}>
          {/* Left: icon + name + meta */}
          <HStack spacing={2} flex={1} minW={0} align="center">
            <Icon as={AppIcons.workflow} boxSize={7} color={isLive ? "green.500" : "purple.500"} flexShrink={0} />
            <Box flex={1} minW={0}>
              <Tooltip label={job.name} placement="top" openDelay={600}>
                <Box display="block" mb={1}>
                  <Text fontSize="md" fontWeight="600" color="gray.800" mb={0} noOfLines={1} textAlign="left">
                    {job.name}
                  </Text>
                </Box>
              </Tooltip>
              <VStack spacing={1} align="flex-start">
                {(showAnalystTags || isLive) && (
                  <Wrap spacing={1} mt={1}>
                    {ownerName && renderAnalystTag(ownerName, job.owner_id)}
                    {sharedAnalysts.map(analyst => renderAnalystTag(analyst.name || analyst.id || "", analyst.id))}
                    {isLive && (
                      <WrapItem>
                        <HStack
                          spacing={1}
                          border="1.5px solid"
                          borderColor="green.500"
                          borderRadius="full"
                          px={2}
                          py="1px"
                          bg="green.50"
                        >
                          <Box
                            w="6px"
                            h="6px"
                            borderRadius="full"
                            bg="green.500"
                            flexShrink={0}
                            animation={`${pulse} 1.8s ease-in-out infinite`}
                          />
                          <Text fontSize="xs" fontWeight="800" color="green.700" mb={0} letterSpacing="0.05em">
                            LIVE
                          </Text>
                        </HStack>
                      </WrapItem>
                    )}
                  </Wrap>
                )}
                <HStack spacing={1} align="center">
                  {modifiedDate && (
                    <Text fontSize="xs" color="gray.400" mb={0} textAlign="left">
                      Last modified {modifiedDate}
                    </Text>
                  )}
                  {job.author_name && (
                    <Tooltip label={job.author_name} placement="bottom" openDelay={600}>
                      <Text fontSize="xs" color="gray.400" mb={0} noOfLines={1} maxW="280px" textAlign="left">
                        by {job.author_name}
                      </Text>
                    </Tooltip>
                  )}
                </HStack>
              </VStack>
            </Box>
          </HStack>

          {/* Right: LIVE badge + optional actions menu */}
          <HStack spacing={1} flexShrink={0} align="center" onClick={e => e.stopPropagation()}>
            {/* Right: Definition btn, Past Runs btn, ⋮ menu */}
            <HStack spacing={1} flexShrink={0} align="center" onClick={e => e.stopPropagation()}>
              {onDefinitionClick && (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  fontWeight="500"
                  fontSize="sm"
                  px={3}
                  leftIcon={<Icon as={AppIcons.workflow} boxSize={3.5} />}
                  _hover={{ bg: "purple.50", borderColor: "purple.700" }}
                  onClick={e => {
                    e.stopPropagation();
                    onDefinitionClick(job);
                  }}
                >
                  Definition
                </Button>
              )}
              {onPastRunsClick && (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  fontWeight="500"
                  fontSize="sm"
                  px={3}
                  leftIcon={<Icon as={AppIcons.workflowRun} boxSize={3.5} />}
                  _hover={{ bg: "purple.50", borderColor: "purple.700" }}
                  onClick={e => {
                    e.stopPropagation();
                    onPastRunsClick(job);
                  }}
                >
                  Past Runs
                </Button>
              )}
            </HStack>

            {hasMenu && (
              <Menu placement="bottom-end" autoSelect={false}>
                <MenuButton
                  as={IconButton}
                  aria-label="Workflow options"
                  icon={<Icon as={AppIcons.menuDots} boxSize={4} />}
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={e => e.stopPropagation()}
                />
                <Portal>
                  <MenuList minW="190px" fontSize="sm" zIndex="dropdown">
                    {onToggleActive && (
                      <MenuItem closeOnSelect={false} onClick={e => e.stopPropagation()}>
                        <HStack justify="space-between" w="100%">
                          <Text mb={0} fontWeight="500">
                            {isLive ? "Live" : "Paused"}
                          </Text>
                          <Switch
                            size="sm"
                            colorScheme="green"
                            isChecked={isLive}
                            onChange={e => {
                              e.stopPropagation();
                              onToggleActive(job, !isLive);
                            }}
                          />
                        </HStack>
                      </MenuItem>
                    )}
                    {onToggleActive && (onRunJob || onCopyJob || onShareClick || onDeleteClick) && (
                      <MenuDivider my={0} />
                    )}
                    {onRunJob && (
                      <MenuItem
                        icon={<AppIcons.runWorkflow />}
                        onClick={e => {
                          e.stopPropagation();
                          onRunJob(job);
                        }}
                      >
                        Run Workflow
                      </MenuItem>
                    )}
                    {onCopyJob && (
                      <MenuItem
                        icon={copyingJobId === job.id ? <Spinner size="xs" /> : <AppIcons.copy />}
                        onClick={e => {
                          e.stopPropagation();
                          onCopyJob(job);
                        }}
                        isDisabled={copyingJobId === job.id}
                      >
                        Duplicate
                      </MenuItem>
                    )}
                    {onShareClick && (
                      <MenuItem
                        icon={<Icon as={AppIcons.share} boxSize={4} />}
                        onClick={e => {
                          e.stopPropagation();
                          onShareClick(job);
                        }}
                      >
                        Share with analyst
                      </MenuItem>
                    )}
                    {onDeleteClick && (onRunJob || onCopyJob || onShareClick) && <MenuDivider my={0} />}
                    {onDeleteClick && (
                      <MenuItem
                        icon={<AppIcons.delete color="red.400" />}
                        color="red.500"
                        isDisabled={isDeleting}
                        onClick={e => {
                          e.stopPropagation();
                          onDeleteClick(job);
                        }}
                      >
                        Delete
                      </MenuItem>
                    )}
                  </MenuList>
                </Portal>
              </Menu>
            )}
          </HStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default JobCard;
